<yc-modal
  [modalHeader]="'CONFIG:hdrRequestToBeForgotten' | ycI18n: 'Request to be Forgotten'"
  [helpLink]="helpLink">
  
  <!-- Search By Email -->
  <div *ngIf="!selectedApplicant"
    [formGroup]="formGroup">
    <div class="mb-3">
      <yc-i18n
        key="CONFIG:textEnterEmailGdprDesc"
        defaultValue="For privacy and data protection reasons, applicants can request to remove their personal data from any application they've submitted or collaborated on. Search for the applicant's email address to view all associated applications.">
      </yc-i18n>
    </div>
    <yc-input
      name="email"
      [label]="'common:textApplicantEmailAddress' | ycI18n: 'Applicant email address'"
      (onChange)="emailChanged()">
    </yc-input>

    <div class="mt-3">
      <yc-button
        [primary]="true"
        [disabled]="formGroup.invalid"
        [langKey]="'common:textSearchApplications'"
        [defaultText]="'Search applications'"
        (onClick)="searchByEmail()">
      </yc-button>
    </div>

    <div *ngIf="didSearch"
      class="mt-4">
      <yc-i18n
        [key]="invalidApplicantEmail ?
          'common:textNoApplicantFoundWithEmail' :
          'common:textNoApplicationsFoundForTheGivenEmail'"
        [defaultValue]="invalidApplicantEmail ?
          'No applicant was found with the email __email__' :
          'No applications were found for this email address.'"
        [context]="{
          email: formGroup.value.email
        }">
      </yc-i18n>
    </div>
  </div>

  <div *ngIf="selectedApplicant">
    <div class="mb-3">
      <gc-applicant-block
        [name]="selectedApplicant.name"
        [email]="selectedApplicant.email"
        [initials]="selectedApplicant.initials"
        [showClear]="true"
        (onClear)="onClearApplicant()">
      </gc-applicant-block>
    </div>

    <div class="mt-3 mb-3">
      <yc-i18n
        key="CONFIG:textSelectAppsGdprDesc2"
        defaultValue="Select the applications to permanently delete. If any have awards and payments tied to them, the payment status must be Cleared, Voided, or Fulfilled to proceed. Once deleted, applications will be removed from most areas. Limited application information will still be available in reporting and Payment Processing with all personal data redacted.">
      </yc-i18n>
    </div>

    <yc-table *ngIf="applications.length > 0"
      [simple]="true"
      [bulkOptions]="bulkOptions"
      [bulkSelected]="bulkSelected"
      [hidePagination]="true"
      [maxHeightBeforeScroll]="'40vh'"
      [key]="'GDPR_' + formGroup.value.email"
      [rows]="applications"
      (bulkSelectedChanged)="bulkSelectedChanged($event)"
      (onHasBulkErrorsChange)="onHasBulkErrorsChange($event)">
      <!-- Application ID -->
      <ng-container *ycTableColumn="'common:textApplicationId';
        prop: 'applicationId',
        let row;">
        <div>
          <yc-button *ngIf="row.canView"
            [link]="true"
            [leftAlignLinkText]="true"
            class="me-3"
            [tooltipText]="'common:textViewApplication' | ycI18n: 'View application'"
            (onClick)="navigateToApp(row)">
            {{ row.applicationId }}
          </yc-button>
          <span *ngIf="!row.canView">
            {{ row.applicationId }}
          </span>
        </div>
      </ng-container>

      <!-- Organization Name -->
      <ng-container *ycTableColumn="'common:labelOrganizationName';
        prop: 'organizationName',
        let row;">
        {{ row.organizationName }}
        <ng-container *ngIf="!row.organizationName">
          <yc-i18n
            key="GLOBAL:textIndividualIsBenefitingFromFunds"
            defaultValue="Individual is benefiting from funds for this program">
          </yc-i18n>
        </ng-container>
      </ng-container>

      <!-- Primary Applicant or Collaborator -->
      <ng-container *ycTableColumn="'common:lblApplicant';
        prop: 'primaryApplicant',
        let row;">
        <yc-i18n
          [key]="row.primaryApplicant ? 'common:textPrimary' : 'common:textCollaborator'"
          [defaultValue]="row.primaryApplicant ? 'Primary' : 'Collaborator'">
        </yc-i18n>
      </ng-container>

      <!-- Has Pending Payments -->
      <ng-container *ycTableColumn="'common:hdrHasPendingPayments';
        prop: 'hasPaymentsPending',
        let row;">
        <yc-i18n
          [key]="row.hasPaymentsPending ? 'common:textYes' : 'common:textNo'"
          [defaultValue]="row.hasPaymentsPending ? 'Yes' : 'No'">
        </yc-i18n>
      </ng-container>
    </yc-table>

    <div *ngIf="!hasBulkErrors && bulkSelected.length > 0"
      class="mt-4">
      <yc-input
        [(value)]="currentDeleteText"
        [label]="'CONFIG:textConfirmDeleteGdpr' | ycI18n: 'To confirm that you want to delete these applications, type __deleteText__ below.': {
          deleteText: desiredDeleteText
        }"
        (valueChange)="setIsValid()">
      </yc-input>
    </div>
  </div>

  <!-- Modal Footer -->
  <div *ycModalFooter
    class="modal-footer justify-content-between">
    <!-- Cancel -->
    <yc-button
      [link]="true"
      [langKey]="'common:btnCancel'"
      [defaultText]="'Cancel'"
      (onClick)="closeModal.emit()">
    </yc-button>

    <!-- Delete -->
    <yc-button
      [primary]="true"
      [textDanger]="true"
      [popoverTemplate]="confirmChangePopoverTemplate"
      [disabled]="!isValid"
      [langKey]="'common:btnDelete'"
      [defaultText]="'Delete'"
      (onPopoverReady)="onPopoverReady($event)">
    </yc-button>

    <!-- Popover Template -->
    <ng-template #confirmChangePopoverTemplate>
      <strong class="mb-3 d-block">
        <yc-i18n
          key="common:textConfirmDeletion"
          defaultValue="Confirm Deletion">
        </yc-i18n>
      </strong>
      <span>
        <yc-i18n
          key="CONFIG:textConfirmRemovalGdpr2"
          defaultValue="Are you sure you want to delete these applications? This action is permanent and cannot be undone.">
        </yc-i18n>
      </span>
      <div class="d-flex justify-content-end align-items-center">
        <!-- No -->
        <yc-button
          class="me-3"
          [link]="true"
          [langKey]="'common:textNo'"
          [defaultText]="'No'"
          (onClick)="popover?.hide()">
        </yc-button>

        <!-- Yes -->
        <yc-button
          [primary]="true"
          [langKey]="'common:textYes'"
          [defaultText]="'Yes'"
          (onClick)="proceedWithRemove()">
        </yc-button>
      </div>
    </ng-template>
  </div>
</yc-modal>